import styles from 'css/src/components/app/VoiceCloningModal.module.scss';

import type { SubmitEventHandler } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { EditVoiceCloneConfig } from './voice-cloning-operations';

import type { AIVoice } from 'js/src/client';
import AIVoiceFilterDropdown from 'js/src/components/app/VoiceSelectionModal/AIVoiceFilterDropdown';
import useAIVoiceFilters from 'js/src/components/app/VoiceSelectionModal/useAIVoiceFilters';
import { BUTTON_TYPE, PrimaryButton, SecondaryButton } from 'js/src/components/common/ui/Button';
import TextInput, {
    TEXT_INPUT_VARIANTS,
    type TypeInputChangeEvent,
} from 'js/src/components/common/ui/TextInput';
import Tooltip, { Placement, Size as TooltipSize } from 'js/src/components/common/ui/Tooltip';

const INFORMATION_TOOLTIP_TEXT =
    'Give your voice a name and describe its language, style, tone, gender, and age so it can be catalogued and easily found later in search.';

type VoiceCloningConfigureStepProps = {
    clonedVoice: AIVoice | null;
    cloneError: string | null;
    isSaving?: boolean;
    onBack: () => void;
    onSave: (config: EditVoiceCloneConfig) => void;
    secondaryLabel?: string;
    initialVoiceName?: string;
    initialLanguageDisplay?: string;
    initialStyleDisplay?: string;
    initialToneDisplay?: string;
    initialGenderDisplay?: string;
    initialAgeDisplay?: string;
};

const VoiceCloningConfigureStep: React.FC<VoiceCloningConfigureStepProps> = ({
    clonedVoice,
    cloneError,
    isSaving = false,
    onBack,
    onSave,
    secondaryLabel = 'Back',
    initialVoiceName,
    initialLanguageDisplay,
    initialStyleDisplay,
    initialToneDisplay,
    initialGenderDisplay,
    initialAgeDisplay,
}) => {
    const [voiceName, setVoiceName] = useState(initialVoiceName ?? '');
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
    const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
    const [selectedTone, setSelectedTone] = useState<string | null>(null);
    const [selectedGender, setSelectedGender] = useState<string | null>(null);
    const [selectedAge, setSelectedAge] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const [dropdownPortalTarget, setDropdownPortalTarget] = useState<HTMLElement | null>(null);
    const audioPreviewPlayedRef = useRef<boolean>(false);

    const handleAudioPreviewPlay = useCallback(() => {
        if (audioPreviewPlayedRef.current) return;
        audioPreviewPlayedRef.current = true;
        analytics.track('Voice Cloning: Audio Preview Played');
    }, []);

    const {
        triggerFetch,
        isUninitialized,
        languageFilterOptions,
        styleFilterOptions,
        toneFilterOptions,
        genderFilterOptions,
        ageFilterOptions,
    } = useAIVoiceFilters();

    useEffect(() => {
        if (isUninitialized) {
            triggerFetch();
        }
    }, [isUninitialized, triggerFetch]);

    useEffect(() => {
        const modal = formRef.current?.closest<HTMLElement>('.modal');
        setDropdownPortalTarget(modal ?? document.body);
    }, []);

    useEffect(() => {
        if (initialLanguageDisplay === undefined) return;
        if (languageFilterOptions.length === 0) return;
        setSelectedLanguage(
            languageFilterOptions.find((o) => o.display === initialLanguageDisplay)?.value ?? null,
        );
    }, [languageFilterOptions, initialLanguageDisplay]);

    useEffect(() => {
        if (initialStyleDisplay === undefined) return;
        if (styleFilterOptions.length === 0) return;
        setSelectedStyle(
            styleFilterOptions.find((o) => o.display === initialStyleDisplay)?.value ?? null,
        );
    }, [styleFilterOptions, initialStyleDisplay]);

    useEffect(() => {
        if (initialToneDisplay === undefined) return;
        if (toneFilterOptions.length === 0) return;
        setSelectedTone(
            toneFilterOptions.find((o) => o.display === initialToneDisplay)?.value ?? null,
        );
    }, [toneFilterOptions, initialToneDisplay]);

    useEffect(() => {
        if (initialGenderDisplay === undefined) return;
        if (genderFilterOptions.length === 0) return;
        setSelectedGender(
            genderFilterOptions.find((o) => o.display === initialGenderDisplay)?.value ?? null,
        );
    }, [genderFilterOptions, initialGenderDisplay]);

    useEffect(() => {
        if (initialAgeDisplay === undefined) return;
        if (ageFilterOptions.length === 0) return;
        setSelectedAge(
            ageFilterOptions.find((o) => o.display === initialAgeDisplay)?.value ?? null,
        );
    }, [ageFilterOptions, initialAgeDisplay]);

    const handleVoiceNameChange = useCallback((e: TypeInputChangeEvent) => {
        setVoiceName(e.target.value);
    }, []);

    const canSave = voiceName.trim().length > 0 && clonedVoice !== null && !isSaving;

    const handleSave = useCallback(() => {
        if (!canSave) return;
        onSave({
            name: voiceName.trim(),
            language: selectedLanguage,
            style: selectedStyle,
            tone: selectedTone,
            gender: selectedGender,
            age: selectedAge,
        });
    }, [
        canSave,
        onSave,
        voiceName,
        selectedLanguage,
        selectedStyle,
        selectedTone,
        selectedGender,
        selectedAge,
    ]);

    const handleSubmit = useCallback<SubmitEventHandler>(
        (e) => {
            e.preventDefault();
            handleSave();
        },
        [handleSave],
    );

    return (
        <form ref={formRef} className="d-flex flex-column gap-4" onSubmit={handleSubmit}>
            <div>
                <h3 className="font-l5 font-l5--h6 color-grey-900 mb-l5-8">AI Voice Preview</h3>
                {cloneError && (
                    <div className="py-l5-12">
                        <span className="font-l5 font-l5--small color-error-500">{cloneError}</span>
                    </div>
                )}
                {clonedVoice?.preview_url && (
                    <audio
                        src={clonedVoice.preview_url}
                        controls
                        className={styles['audio-preview']}
                        data-testid="voice-configure-audio-preview"
                        onPlay={handleAudioPreviewPlay}
                    />
                )}
            </div>

            <div>
                <h3 className="font-l5 font-l5--h6 color-grey-900 mb-l5-8 d-flex align-items-center">
                    Information
                    <Tooltip
                        title={INFORMATION_TOOLTIP_TEXT}
                        trigger="hover"
                        placement={Placement.RIGHT}
                        size={TooltipSize.LARGE}
                    >
                        <i className="material-symbols-outlined ms-l5-4 text-muted d-flex align-items-center">
                            help
                        </i>
                    </Tooltip>
                </h3>

                <div className="d-flex flex-column gap-3">
                    <div className="d-flex gap-3">
                        <div className={styles['form-field']}>
                            <label className="font-l5 font-l5--subtitle-sm color-grey-900 mb-l5-8">
                                Voice name
                            </label>
                            <TextInput
                                variant={TEXT_INPUT_VARIANTS.STANDARD}
                                text={voiceName}
                                onChange={handleVoiceNameChange}
                                placeholder="e.g. Reese"
                                data-testid="voice-configure-name-input"
                            />
                        </div>
                        <div className={styles['form-field']}>
                            <label className="font-l5 font-l5--subtitle-sm color-grey-900 mb-l5-8">
                                Language
                            </label>
                            <AIVoiceFilterDropdown
                                options={languageFilterOptions}
                                selectedValue={selectedLanguage}
                                onChange={setSelectedLanguage}
                                placeholder="Select language"
                                menuPortalTarget={dropdownPortalTarget}
                                includeAny={false}
                            />
                        </div>
                        <div className={styles['form-field']}>
                            <label className="font-l5 font-l5--subtitle-sm color-grey-900 mb-l5-8">
                                Style
                            </label>
                            <AIVoiceFilterDropdown
                                options={styleFilterOptions}
                                selectedValue={selectedStyle}
                                onChange={setSelectedStyle}
                                placeholder="Select style"
                                menuPortalTarget={dropdownPortalTarget}
                                includeAny={false}
                            />
                        </div>
                    </div>
                    <div className="d-flex gap-3">
                        <div className={styles['form-field']}>
                            <label className="font-l5 font-l5--subtitle-sm color-grey-900 mb-l5-8">
                                Tone
                            </label>
                            <AIVoiceFilterDropdown
                                options={toneFilterOptions}
                                selectedValue={selectedTone}
                                onChange={setSelectedTone}
                                placeholder="Select tone"
                                menuPortalTarget={dropdownPortalTarget}
                                includeAny={false}
                            />
                        </div>
                        <div className={styles['form-field']}>
                            <label className="font-l5 font-l5--subtitle-sm color-grey-900 mb-l5-8">
                                Gender
                            </label>
                            <AIVoiceFilterDropdown
                                options={genderFilterOptions}
                                selectedValue={selectedGender}
                                onChange={setSelectedGender}
                                placeholder="Select gender"
                                menuPortalTarget={dropdownPortalTarget}
                                includeAny={false}
                            />
                        </div>
                        <div className={styles['form-field']}>
                            <label className="font-l5 font-l5--subtitle-sm color-grey-900 mb-l5-8">
                                Age
                            </label>
                            <AIVoiceFilterDropdown
                                options={ageFilterOptions}
                                selectedValue={selectedAge}
                                onChange={setSelectedAge}
                                placeholder="Select age"
                                menuPortalTarget={dropdownPortalTarget}
                                includeAny={false}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-center gap-2">
                <SecondaryButton onClick={onBack} text={secondaryLabel} />
                <PrimaryButton
                    text="Save voice"
                    disabled={!canSave || isSaving}
                    type={BUTTON_TYPE.SUBMIT}
                />
            </div>
        </form>
    );
};

export default VoiceCloningConfigureStep;
